import { useState } from 'react';
import { useSnackbar } from 'notistack';

import type { Resource } from '@setvi/shared/interfaces';

import { usePrintOrderCart } from '../../../hooks/use-print-order-cart';
import type { PrintOrderItemCustomization } from '../../../types';
import { useCoverSection } from './use-cover-section';
import { useCustomizePreview } from './use-customize-preview';
import { useQrSection } from './use-qr-section';

interface UseCustomizeDialogArgs {
  resource: Resource;
  initial?: PrintOrderItemCustomization;
  isEdit: boolean;
  onClose: () => void;
}

/**
 * Composes the two customization sections and the preview beside them, and
 * owns what only makes sense across both.
 *
 * An item carries AT MOST ONE customization. QR is active for QR-capable
 * assets until the rep turns the cover page on and generates one, which takes
 * over, and generating QR drops the cover again.
 */
export const useCustomizeDialog = ({
  resource,
  initial,
  isEdit,
  onClose
}: UseCustomizeDialogArgs) => {
  const { enqueueSnackbar } = useSnackbar();
  const { addItem, updateItem, isSyncing } = usePrintOrderCart();

  const hasQrOption = !!resource.HasQRCode;
  const hasCoverOption = !!resource.HasCoverPage;

  const [accepted, setAccepted] = useState(false);

  const qr = useQrSection({ resource, initial, enabled: hasQrOption });
  const cover = useCoverSection({ resource, initial, enabled: hasCoverOption });

  // A cover counts only once turned on and generated; a picked-but-pending one
  // leaves QR on.
  const addCover = cover.active && !!cover.templateId && !!cover.previewUrl;
  const addQr = hasQrOption && !addCover;

  const hasQrPreview = addQr && qr.machines.length > 0;
  // A cover is in the preview only once its PDF exists — a "Cover" entry with
  // no pdf would fall back to the raw asset and masquerade as the cover.
  const hasCoverPreview = !!cover.templateId && !!cover.previewUrl;

  const preview = useCustomizePreview({
    resource,
    hasQrPreview,
    hasCoverPreview,
    coverPdfUrl: cover.previewUrl,
    coverPageCount: cover.pageCount,
    machines: qr.machines,
    qrSheetUrl: qr.sheetUrl
  });

  // The one place the two sections meet: whichever the rep acts on wins, and
  // the other is dropped along with the preview position.
  const generateQr = async () => {
    if (!(await qr.generate())) return;
    cover.clear();
    preview.setPage(0);
  };

  const selectCover = async (id: string) => {
    if (!(await cover.select(id))) return;
    qr.clear();
    preview.setPage(0);
  };

  // QR needs a generated sheet matching the CURRENT input plus accepted notes.
  // The cover is optional: with the toggle off it never blocks. With it on, the
  // rep has asked for one, so at least one detail must be filled in and a cover
  // picked (which generates it) — the asset cannot be added without the cover
  // they meant to have.
  const qrReady =
    !addQr ||
    (qr.machines.length > 0 && accepted && !!qr.fileName && qr.isCurrent);
  const coverReady = !cover.active || (cover.hasAnyField && addCover);

  const handleConfirm = async () => {
    const customization = {
      addQr,
      qrNumbers: qr.numbers,
      addCover,
      coverTemplateId: cover.templateId,
      qrFileName: addQr ? qr.fileName : undefined,
      qrSheetUrl: addQr ? qr.sheetUrl : undefined,
      coverFileName: addCover ? cover.fileName : undefined,
      coverPreviewUrl: addCover ? cover.previewUrl : undefined,
      coverFields: addCover ? cover.form : undefined
    };

    try {
      if (isEdit) {
        await updateItem(resource.ID, customization);
        enqueueSnackbar('Changes saved.', { variant: 'success' });
      } else {
        await addItem(resource, customization);
        enqueueSnackbar('Added to cart.', { variant: 'success' });
      }
      onClose();
    } catch {
      // Keep the dialog open on failure; the mutation cache toasts the error.
    }
  };

  // Hint under the asset's own preview before anything is generated, tailored
  // to what this asset supports.
  const emptyMessage = (() => {
    if (hasQrOption && hasCoverOption)
      return 'Showing the original asset. Generate QR codes or a cover page to preview them.';
    if (hasQrOption)
      return 'Showing the original asset. Generate QR codes to preview them.';
    return 'Showing the original asset. Turn on the cover page and select one to preview it.';
  })();

  return {
    hint:
      hasQrOption || hasCoverOption
        ? ''
        : 'No customization options are available for this asset.',
    qr: {
      ...qr,
      hasOption: hasQrOption,
      // Chips mirror the input; the preview mirrors the generated sheet.
      machines: qr.inputMachines,
      // The exclusive version, which drops the cover on success.
      generate: generateQr,
      // Jump the preview to that chip's page. The chip may not be in the
      // generated sheet yet (before a regenerate), so it is looked up; QR pages
      // start after the cover's.
      onChipClick: (index: number) => {
        const generatedIndex = qr.machines.indexOf(qr.inputMachines[index]);
        if (generatedIndex < 0) return;
        preview.setPage(preview.qrPageOffset + generatedIndex);
      }
    },
    cover: {
      ...cover,
      hasOption: hasCoverOption,
      // The exclusive version, which drops the generated QR sheet.
      select: selectCover,
      // Off resets the section, so the preview goes back to the first page.
      setActive: (next: boolean) => {
        cover.setActive(next);
        if (!next) preview.setPage(0);
      },
      // The logo dropzone validates type and size; surface its rejections.
      onLogoError: (message: string) =>
        enqueueSnackbar(message, { variant: 'error' })
    },
    notes: {
      visible: addQr,
      accepted,
      setAccepted
    },
    preview: {
      generating: qr.generating,
      // The cover list's own loading shows inside the cover section, so the
      // "select a cover" prompt is not hidden on open.
      coverLoading: cover.generating,
      hasPreview: preview.hasPreview,
      previewDocument: preview.document,
      pageIndex: preview.pageIndex,
      previewPages: preview.pages,
      emptyMessage,
      onPageChange: preview.setPage
    },
    footer: {
      // Blocked mid-generation so a confirm cannot save an item without its
      // cover.
      canContinue:
        qrReady && coverReady && !cover.generating && !cover.hasErrors,
      isSyncing,
      handleConfirm
    }
  };
};
